import React, { useState } from 'react';
import { AdsReportRow } from './useAdsStats.ts';
import { useToast } from '../../core/contexts/ToastContext.tsx';

interface CopyForSheetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: AdsReportRow[];
}

const STATUS_LABEL_MAP: Record<string, string> = {
  POLICY: 'Banned',
  Policy: 'Banned',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  BANNED: 'Banned',
  DISAPPROVED: 'Rejected',
  REJECTED: 'Rejected',
  PENDING_REVIEW: 'Preparing',
  IN_PROCESS: 'Preparing',
  PREAPPROVED: 'Preparing',
  SCHEDULED: 'Scheduled',
  WITH_ISSUES: 'Not delivering',
  NOT_DELIVERING: 'Not delivering',
  PENDING_BILLING_INFO: 'Unsettled',
};

export function CopyForSheetsModal({ isOpen, onClose, report }: CopyForSheetsModalProps) {
  const [metric, setMetric] = useState<'leads' | 'app_installs' | null>(null);
  const [fallbackText, setFallbackText] = useState<string | null>(null);
  const { showToast } = useToast();

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!metric) return;

    // Filter out total rows if any exist
    const rows = report.filter(r => r.campaign_name !== 'TOTAL' && r.ad_id !== 'TOTAL' && r.ad_name !== 'Разом');

    const tsvRows = rows.map(r => {
      const rawStatus = r.ad_status || '';
      // Preserve current delivery labels (including Campaign_* and Ad_OFF) for the parser.
      const status = STATUS_LABEL_MAP[rawStatus] || rawStatus;
      const campaignName = r.campaign_name || '';
      const spend = r.spend==null?'':r.spend.toFixed(2);
      const metricValue = metric === 'leads' ? r.leads : r.app_installs;
      const purchases = r.purchases;

      return `${status}\t${campaignName}\t${spend}\t${metricValue??''}\t${purchases??''}`;
    });

    const tsv = tsvRows.join('\n');

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(tsv);
        showToast(`Скопійовано ${tsvRows.length} рядків у буфер обміну`, 'success');
        onClose();
      } else {
        setFallbackText(tsv);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setFallbackText(tsv);
    }
  };

  const handleSelectAll = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.select();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <h2 style={{ marginBottom: 'var(--space-3)' }}>Копіювати для Google Таблиць</h2>

        {fallbackText ? (
          <div>
            <p className="text-muted text-sm" style={{ marginBottom: 'var(--space-2)' }}>
              Браузер не дозволяє автоматичне копіювання. Виділіть текст нижче і натисніть Ctrl+C (або Cmd+C).
            </p>
            <textarea
              readOnly
              value={fallbackText}
              onFocus={handleSelectAll}
              style={{
                width: '100%',
                height: '200px',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.75rem',
                padding: 'var(--space-2)',
                marginBottom: 'var(--space-4)',
                background: 'var(--color-surface-1)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn--secondary" onClick={onClose}>
                Закрити
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 600 }}>Оберіть метрику для копіювання (Leads або App Installs):</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="copy-metric"
                    value="leads"
                    checked={metric === 'leads'}
                    onChange={() => setMetric('leads')}
                  />
                  Leads
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="copy-metric"
                    value="app_installs"
                    checked={metric === 'app_installs'}
                    onChange={() => setMetric('app_installs')}
                  />
                  App Installs
                </label>
              </div>
            </div>

            <p className="text-muted text-sm" style={{ marginBottom: 'var(--space-4)' }}>
              Буде скопійовано: Status, Campaign, Spend, {metric === 'leads' ? 'Leads' : metric === 'app_installs' ? 'App Installs' : '{обране}'}, Purchases — без заголовка і підсумкового рядка
            </p>

            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button className="btn btn--ghost" onClick={onClose}>
                Скасувати
              </button>
              <button
                className="btn btn--primary"
                disabled={!metric}
                onClick={handleCopy}
              >
                Скопіювати
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
